import React, { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Button, ButtonVariant } from '../../../common/Button'
import { useModal } from '../../../../hooks/useModal'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { Card } from '../../../common/Card'
import { Avatar } from '../../../common/Avatar'
import { TimeSlotTime } from '../TimeSlotTime'
import { theme } from '../../../../theme/theme'
// import { capitalize } from '../../../../helpers/string'
// import { formatDateWithFormat } from '../../../../helpers/date'
import { IBookingModel } from '../../../../interfaces/models/bookings.interfaces'
import { IFacilityWithFiles } from '../../../../interfaces/services/facilities.interfaces'
import { DeleteBookingConfirmationModal } from '../DeleteBookingConfirmationModal'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { bookingQueries, deleteBooking } from '../../../../api/bookings'
import { ToastType } from '../../../common/Toast/toast-type'
import { useSelectedSocietyId } from '../../../../hooks/useSelectedSocietyContext'
import { unitQueries } from '../../../../api/units'
import { useAuth } from '../../../../hooks/useAuth'
import { futureBookingsForUnitsAndFacility } from '../../../../state/stores/bookings'
import { Badge } from '../../../common/Badge'
import { getRuleAsInteger } from '../../../../state/models/facility'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../../helpers/society'
import { societyQueries } from '../../../../api/societies'

interface FacilityCalendarBookingCardProps {
  type: 'booking' | 'timeSlot'
  booking?: IBookingModel
  facility: IFacilityWithFiles
  createBooking?: (isAdmin: boolean) => void
  startDate: Date
  endDate: Date
  loading?: boolean
}

export const FacilityCalendarBookingCard = ({
  type,
  booking,
  facility,
  createBooking,
  startDate,
  endDate,
  loading,
}: FacilityCalendarBookingCardProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const {
    show: showDeleteBookingModal,
    open: openDeleteBookingModal,
    close: closeDeleteBookingModal,
  } = useModal()

  const { data: society } = useQuery(
    societyQueries.getSociety(facility.societyId)
  )

  const { userId } = useAuth()
  const userIsBoard = isUserBoardMemberInSociety(society, userId as string)
  const userIsAdmin = isUserAdminInSociety(society, userId as string)
  const canBookAsAdmin = userIsAdmin || userIsBoard

  const { setToastNotification } = useToastNotifications()
  const client = useQueryClient()
  const { mutate: mutateDeleteBooking, isPending: isDeletingBooking } =
    useMutation({
      mutationFn: deleteBooking,
      onSuccess: async () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('facilityView.bookings.flashMessage.deleteBookingSuccess')
        )
        closeDeleteBookingModal()

        await client.invalidateQueries({ queryKey: [bookingQueries.scope] })
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('facilityView.bookings.flashMessage.deleteBookingFailure')
        )
      },
    })
  const onDeleteBooking = async (): Promise<void> => {
    booking && mutateDeleteBooking(booking._id)
  }
  const isBooking = type === 'booking'

  const { selectedSocietyId } = useSelectedSocietyId()
  const { data: userUnits, isLoading: isLoadingUserUnits } = useQuery(
    unitQueries.getUserUnits(userId)
  )
  const userUnitsForSociety = userUnits?.filter(
    (_unit) => _unit.societyId === selectedSocietyId
  )
  const { data: facilityBookings, isLoading: isLoadingBookings } = useQuery(
    bookingQueries.getBookingsPerFacility(facility._id)
  )

  const userHasReachedMaxNumberOfBookings = useCallback((): boolean => {
    if (!facility) {
      return false
    }
    if (!userUnitsForSociety) {
      return false
    }
    if (facility?.rules === 'unlimited') {
      return false
    }

    const futureFacilityBookings = futureBookingsForUnitsAndFacility(
      userUnitsForSociety.map((_unit) => _unit._id),
      facilityBookings || [],
      facility._id
    )
    const maxNumberOfBookings = getRuleAsInteger(facility.rules)

    return futureFacilityBookings.length >= maxNumberOfBookings
  }, [facility, facilityBookings, userUnitsForSociety])

  if (isBooking && !booking && isLoadingUserUnits && isLoadingBookings) {
    return null
  }

  const dateTitle = isBooking
    ? dayjs(new Date(startDate)).utc().format('dddd D MMMM')
    : dayjs(startDate).utc().format('dddd D MMMM')

  return (
    <Card minWidth={400} className="grow xl:max-w-md">
      {{
        body: (
          <div className="flex shrink items-center gap-4 p-4">
            {facility.coverPhoto && (
              <Avatar url={facility.coverPhoto.url} size={50} />
            )}
            <div className="flex grow flex-col items-start justify-center">
              {booking?.isAdmin && (
                <Badge
                  padding="px-1.5"
                  colorClass="bg-brandGreen/10"
                  borderColorClass="border-brandGreen"
                  textColorClass="text-black"
                  text="Admin"
                  className="justify-self-start mb-1"
                />
              )}
              <p
                style={theme.textVariants.base}
                className="text-neutral-40 capitalize"
              >
                {dateTitle}
              </p>
              {facility.bookingGranularity === 'full-day' ? (
                <p style={theme.textVariants.base}>
                  {translate('facilityView.calendar.fullDay')}
                </p>
              ) : (
                <TimeSlotTime
                  startDate={isBooking ? new Date(startDate) : startDate}
                  endDate={isBooking ? new Date(endDate) : endDate}
                  prefix="Kl."
                />
              )}
            </div>
            {!isBooking && createBooking && (
              <Button
                wrapperClassName="shrink justify-self-end"
                label={translate('common.actions.book')}
                disabled={userHasReachedMaxNumberOfBookings()}
                variant={ButtonVariant.PRIMARY}
                onClick={() => createBooking(false)}
                loading={isDeletingBooking || loading}
              />
            )}
            {!isBooking && createBooking && canBookAsAdmin && (
              <Button
                wrapperClassName="shrink justify-self-end"
                label={translate('common.actions.bookAsAdmin')}
                variant={ButtonVariant.PRIMARY}
                onClick={() => createBooking(true)}
                loading={isDeletingBooking || loading}
              />
            )}
            {isBooking && booking && (
              <>
                <Button
                  wrapperClassName="shrink justify-self-end"
                  label={translate(
                    'facilityView.bookings.buttons.deleteBooking'
                  )}
                  variant={ButtonVariant.DANGER}
                  onClick={openDeleteBookingModal}
                  loading={isDeletingBooking || loading}
                />

                <DeleteBookingConfirmationModal
                  onDelete={onDeleteBooking}
                  showModal={showDeleteBookingModal}
                  closeModal={closeDeleteBookingModal}
                  disableCloseOnConfirm
                  booking={booking}
                />
              </>
            )}
          </div>
        ),
      }}
    </Card>
  )
}
